<template>
  <div class="ai-tutor-system">
    <!-- 背景装饰 -->
    <div class="bg-decoration">
      <div class="bg-circle bg-circle-1"></div>
      <div class="bg-circle bg-circle-2"></div>
      <div class="bg-circle bg-circle-3"></div>
    </div>

    <div class="floating-elements">
      <div
        v-for="(element, index) in floatingElements"
        :key="index"
        class="floating-element"
        :style="element.style"
      ></div>
    </div>

    <!-- Hero Section -->
    <section class="hero-section">
      <div class="hero-container">
        <div class="hero-badge">
          <span class="hero-badge-dot"></span>
          <span>课后AI辅导系统 V2.0</span>
        </div>
        
        <div class="hero-mascot">
          <div class="mascot-container">
            <div class="mascot-text">AI</div>
          </div>
        </div>
        
        <h1 class="hero-title">中国-东盟建筑工程技术专业<br>课后AI辅导系统</h1>
        <p class="hero-subtitle">苏格拉底式引导学习，多模态智能交互，支持中英双语，专业针对性辅导</p>
        
        <div class="language-controls">
          <button 
            class="language-btn"
            :class="{ active: currentLanguage === 'zh' }"
            @click="switchLanguage('zh')"
          >中文</button>
          <button 
            class="language-btn"
            :class="{ active: currentLanguage === 'en' }"
            @click="switchLanguage('en')"
          >English</button>
        </div>
      </div>
    </section>

    <!-- Course Selection -->
    <section class="course-section">
      <div class="section-header">
        <h2 class="section-title">
          <span class="section-title-icon">📚</span>
          <span class="section-title-text">选择专业课程</span>
        </h2>
        <p class="section-subtitle">选择您要学习的建筑工程技术专业课程，开始智能辅导</p>
      </div>
      <div class="course-grid">
        <div
          v-for="course in courses"
          :key="course.id"
          class="course-card"
          :class="{ active: currentCourse === course.id }"
          @click="selectCourse(course.id)"
        >
          <div class="course-icon">{{ course.icon }}</div>
          <div class="course-title">{{ course.title }}</div>
          <div class="course-desc">{{ course.description }}</div>
        </div>
      </div>
    </section>

    <!-- Main Chat Section -->
    <section class="chat-section">
      <div class="chat-container">
        <div class="chat-header">
          <div class="chat-title">
            <div class="chat-title-text">AI智能辅导助手</div>
            <div class="chat-badge">苏格拉底式引导</div>
          </div>
          <div class="chat-controls">
            <button class="chat-control-btn" title="清空对话" @click="clearChat">🗑️</button>
            <button class="chat-control-btn" title="导出对话" @click="exportChat">📄</button>
            <button class="chat-control-btn" title="设置" @click="openSettings">⚙️</button>
          </div>
        </div>

        <div class="chat-messages" ref="chatMessages">
          <div
            v-for="(message, index) in messages"
            :key="index"
            class="message"
            :class="message.sender"
          >
            <div class="message-avatar" :class="message.sender">
              {{ message.sender === 'user' ? 'U' : 'AI' }}
            </div>
            <div class="message-content">
              <div v-html="message.content"></div>
              <div v-if="message.socraticQuestion" class="socratic-question">
                <div class="socratic-label">🤔 {{ message.sender === 'user' ? '引导思考' : '深入思考' }}</div>
                {{ message.socraticQuestion }}
              </div>
            </div>
          </div>
          
          <!-- Typing Indicator -->
          <div v-if="isTyping" class="message">
            <div class="message-avatar ai">AI</div>
            <div class="message-content">
              <div class="typing-indicator">
                <span>AI正在思考</span>
                <div class="typing-dots">
                  <div class="typing-dot"></div>
                  <div class="typing-dot"></div>
                  <div class="typing-dot"></div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="chat-input-area">
          <div class="input-controls">
            <button
              v-for="mode in inputModes"
              :key="mode.id"
              class="input-mode-btn"
              :class="{ active: currentInputMode === mode.id }"
              @click="switchInputMode(mode.id)"
            >
              <span>{{ mode.icon }}</span>
              <span>{{ mode.label }}</span>
            </button>
          </div>

          <!-- Text Input Mode -->
          <div v-show="currentInputMode === 'text'" class="input-mode-content">
            <div class="chat-input-wrapper">
              <textarea
                v-model="inputMessage"
                class="chat-input"
                :placeholder="currentLanguage === 'zh' ? '请输入您的问题或回答...' : 'Please enter your question or answer...'"
                @keydown.enter.prevent="handleEnterKey"
                @input="adjustTextareaHeight"
                ref="chatInput"
              ></textarea>
              <button class="send-button" @click="sendMessage" :disabled="!inputMessage.trim()">
                <svg viewBox="0 0 24 24">
                  <path d="M2 21l21-9L2 3v7l15 2-15 2v7z"/>
                </svg>
              </button>
            </div>
          </div>

          <!-- Voice Input Mode -->
          <div v-show="currentInputMode === 'voice'" class="input-mode-content">
            <div class="voice-control">
              <button
                class="voice-button"
                :class="{ recording: isRecording }"
                @click="toggleVoiceRecording"
              >🎤</button>
              <div class="voice-status">{{ voiceStatus }}</div>
            </div>
          </div>

          <!-- Handwrite Input Mode -->
          <div v-show="currentInputMode === 'handwrite'" class="input-mode-content">
            <div
              class="file-upload-area"
              @click="selectFile('handwrite')"
              @dragover.prevent="handleDragOver"
              @dragleave.prevent="handleDragLeave"
              @drop.prevent="handleDrop($event, 'handwrite')"
            >
              <div class="file-upload-icon">✍️</div>
              <div class="file-upload-text">
                <div>拖拽手写图片到此处或点击上传</div>
                <div class="file-upload-hint">
                  支持 JPG、PNG、PDF 格式
                </div>
              </div>
            </div>
          </div>

          <!-- Photo Input Mode -->
          <div v-show="currentInputMode === 'photo'" class="input-mode-content">
            <div
              class="file-upload-area"
              @click="selectFile('photo')"
              @dragover.prevent="handleDragOver"
              @dragleave.prevent="handleDragLeave"
              @drop.prevent="handleDrop($event, 'photo')"
            >
              <div class="file-upload-icon">📷</div>
              <div class="file-upload-text">
                <div>拍照或上传图片进行题目识别</div>
                <div class="file-upload-hint">
                  支持题目识别、手写公式识别等
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Features Section Component -->
    <FeaturesSection 
      :features="features"
      :clickable="true"
      @feature-click="handleFeatureClick"
    />

    <!-- Exercise Recommendations -->
    <section class="exercise-section">
      <div class="exercise-container">
        <div class="section-header">
          <h2 class="section-title">
            <span class="section-title-icon">📝</span>
            <span class="section-title-text">智能练习推荐</span>
          </h2>
          <p class="section-subtitle">AI根据学习进度智能推荐相关练习题，巩固知识点</p>
        </div>
        <div class="exercise-grid">
          <div
            v-for="exercise in exercises"
            :key="exercise.id"
            class="exercise-card"
            @click="loadExercise(exercise.id)"
          >
            <div class="exercise-header">
              <div class="exercise-title">{{ exercise.title }}</div>
              <div
                class="difficulty-badge"
                :class="`difficulty-${exercise.difficulty}`"
              >{{ exercise.difficultyLabel }}</div>
            </div>
            <div class="exercise-meta">
              <span>⏱️ {{ exercise.duration }}</span>
              <span>{{ exercise.typeIcon }} {{ exercise.type }}</span>
              <span>🎯 {{ exercise.category }}</span>
            </div>
            <div class="exercise-desc">{{ exercise.description }}</div>
          </div>
        </div>
      </div>
    </section>

    <!-- Statistics Section Component -->
    <StatisticsSection 
      ref="statsComponent"
      :statistics="statistics"
      :clickable="true"
      :animate-numbers="true"
      @stat-click="handleStatClick"
    />

    <!-- Hidden file input -->
    <input
      ref="fileInput"
      type="file"
      style="display: none"
      accept="image/*,.pdf"
      @change="handleFileSelect"
    />
  </div>
</template>

<script>
// 导入拆分的组件
import FeaturesSection from './components/FeaturesSection.vue'
import StatisticsSection from './components/StatisticsSection.vue'

export default {
  name: 'AITutorSystem',
  components: {
    FeaturesSection,
    StatisticsSection
  },
  data() {
    return {
      currentLanguage: 'zh',
      currentInputMode: 'text',
      currentCourse: 'construction',
      isRecording: false,
      isTyping: false,
      inputMessage: '',
      voiceStatus: '点击按钮开始语音输入',
      socraticLevel: 0,
      floatingElements: [],
      messages: [
        {
          sender: 'ai',
          content: '你好！我是你的AI辅导助手，专注于中国-东盟建筑工程技术专业教学。我会用苏格拉底式问答方法引导你深入思考。',
          socraticQuestion: '在开始学习建筑施工技术之前，你能告诉我为什么钢筋和混凝土能够很好地协同工作吗？'
        }
      ],
      courses: [
        {
          id: 'construction',
          icon: '🏗️',
          title: '建筑施工技术',
          description: '钢筋混凝土、砌体结构、预制装配式等施工技术'
        },
        {
          id: 'materials',
          icon: '🧱',
          title: '建筑材料',
          description: '建筑材料性能、检测方法、选用标准'
        },
        {
          id: 'structure',
          icon: '🏢',
          title: '建筑结构',
          description: '结构设计原理、构造要求、计算方法'
        },
        {
          id: 'bim',
          icon: '💻',
          title: 'BIM应用技术',
          description: 'BIM建模、协同设计、施工管理应用'
        },
        {
          id: 'management',
          icon: '📊',
          title: '施工组织管理',
          description: '施工组织、进度控制、质量安全管理'
        }
      ],
      inputModes: [
        { id: 'text', icon: '💬', label: '文字输入' },
        { id: 'voice', icon: '🎤', label: '语音输入' },
        { id: 'handwrite', icon: '✍️', label: '手写识别' },
        { id: 'photo', icon: '📷', label: '拍照识别' }
      ],
      features: [
        {
          id: 1,
          icon: '🤔',
          title: '苏格拉底式问答',
          description: '通过3轮以上连续引导式提问，培养学生批判性思维和深度理解能力'
        },
        {
          id: 2,
          icon: '🌏',
          title: '多语言实时辅导',
          description: '支持中文、English双语切换，实时翻译，满足国际化教学需求'
        },
        {
          id: 3,
          icon: '🎤',
          title: '多模态交互',
          description: '支持语音提问、手写拍照识别、文字输入等多种答疑方式'
        },
        {
          id: 4,
          icon: '📝',
          title: '智能题目生成',
          description: '根据课程进度针对性生成不同难度的课后练习题并提供辅导'
        },
        {
          id: 5,
          icon: '📱',
          title: '移动端优化',
          description: '完美适配移动设备，4G网络下流畅使用，随时随地学习'
        },
        {
          id: 6,
          icon: '🌐',
          title: '多终端支持',
          description: '支持Chrome/Firefox/Safari/Edge等主流浏览器，跨平台兼容'
        }
      ],
      exercises: [
        {
          id: 'exercise1',
          title: '钢筋混凝土梁配筋计算',
          difficulty: 'medium',
          difficultyLabel: '中等',
          duration: '15分钟',
          typeIcon: '📊',
          type: '计算题',
          category: '结构设计',
          description: '某钢筋混凝土梁，跨度6m，承受均布荷载，请计算主筋配筋面积...',
          question: '某钢筋混凝土梁，跨度6m，承受均布荷载25kN/m，混凝土强度等级C30，钢筋采用HRB400，请计算主筋配筋面积。'
        },
        {
          id: 'exercise2',
          title: '混凝土施工质量控制',
          difficulty: 'easy',
          difficultyLabel: '简单',
          duration: '10分钟',
          typeIcon: '📝',
          type: '简答题',
          category: '施工技术',
          description: '请简述混凝土浇筑过程中的质量控制要点，说明如何预防常见问题...',
          question: '请简述混凝土浇筑过程中的质量控制要点，并说明如何预防常见的质量问题。'
        },
        {
          id: 'exercise3',
          title: '钢筋连接方式对比',
          difficulty: 'hard',
          difficultyLabel: '困难',
          duration: '25分钟',
          typeIcon: '🔍',
          type: '分析题',
          category: '标准对比',
          description: '对比分析中国标准与东盟标准中钢筋连接方式的异同点...',
          question: '比较分析钢筋机械连接、焊接连接和绑扎连接三种方式的优缺点及适用场合，结合中国标准和东盟标准进行对比。'
        }
      ],
      statistics: [
        { id: 1, value: '25', label: '今日问答次数' },
        { id: 2, value: '8', label: '完成练习题数' },
        { id: 3, value: '3.5h', label: '今日学习时长' },
        { id: 4, value: '92%', label: '知识掌握度' },
        { id: 5, value: '15', label: '苏格拉底式引导' }
      ],
      conversationHistory: [],
      speechRecognition: null,
      currentFileMode: ''
    }
  },
  mounted() {
    this.initializeApp()
    this.createFloatingElements()
    this.loadConversationHistory()
  },
  beforeDestroy() {
    if (this.speechRecognition) {
      this.speechRecognition.stop()
    }
  },
  methods: {
    initializeApp() {
      console.log('AI辅导系统V2.0初始化完成')
    },
    
    switchLanguage(lang) {
      this.currentLanguage = lang
      console.log('切换语言为:', lang)
    },
    
    selectCourse(courseId) {
      this.currentCourse = courseId
      this.socraticLevel = 0
      
      const course = this.courses.find(c => c.id === courseId)
      if (course) {
        this.addMessage('ai', `欢迎学习${course.title}！我将根据这门课程的特点为你提供专业的辅导。有什么问题尽管问我吧！`)
      }
    },
    
    switchInputMode(mode) {
      this.currentInputMode = mode
      if (mode === 'voice') {
        this.voiceStatus = '点击按钮开始语音输入'
      }
    },
    
    handleEnterKey(event) {
      if (!event.shiftKey) {
        this.sendMessage()
      }
    },
    
    adjustTextareaHeight(event) {
      const textarea = event.target
      textarea.style.height = 'auto'
      textarea.style.height = Math.min(textarea.scrollHeight, 120) + 'px'
    },
    
    sendMessage() {
      const message = this.inputMessage.trim()
      if (!message) return
      
      this.addMessage('user', message)
      this.inputMessage = ''
      
      // Reset textarea height
      if (this.$refs.chatInput) {
        this.$refs.chatInput.style.height = 'auto'
      }
      
      this.isTyping = true
      
      // Simulate AI response
      setTimeout(() => {
        this.isTyping = false
        this.generateAIResponse(message)
      }, 1500 + Math.random() * 1000)
      
      // Save to history
      this.conversationHistory.push({
        role: 'user',
        content: message,
        timestamp: new Date().toISOString(),
        course: this.currentCourse
      })
      
      this.saveConversationHistory()
    },
    
    addMessage(sender, content, socraticQuestion = null) {
      const message = {
        sender,
        content,
        socraticQuestion
      }
      
      this.messages.push(message)
      
      // Scroll to bottom
      this.$nextTick(() => {
        const messagesContainer = this.$refs.chatMessages
        if (messagesContainer) {
          messagesContainer.scrollTop = messagesContainer.scrollHeight
        }
      })
    },
    
    generateAIResponse(userMessage) {
      this.socraticLevel++
      
      let response = ""
      let useSocratic = this.socraticLevel <= 3
      
      const courseContext = {
        'construction': '施工技术',
        'materials': '建筑材料',
        'structure': '建筑结构',
        'bim': 'BIM技术',
        'management': '施工管理'
      }
      
      // Generate contextual response
      const course = this.courses.find(c => c.id === this.currentCourse)
      if (this.currentCourse === 'construction') {
        if (userMessage.includes('钢筋') || userMessage.includes('混凝土')) {
          response = "很好的思考！钢筋和混凝土确实是很好的组合材料。钢筋提供抗拉强度，混凝土提供抗压强度，它们的热膨胀系数相近，这使得它们能够协同工作。"
        } else if (userMessage.includes('施工') || userMessage.includes('浇筑')) {
          response = "你对施工工艺的理解很有见地。在实际工程中，我们需要考虑多个因素来确保施工质量和安全。"
        } else {
          response = `这是一个关于${courseContext[this.currentCourse]}的很好的问题。让我们一起来探讨这个话题。`
        }
      } else {
        response = `关于${course ? course.title : '这个话题'}，让我为你详细解答。`
      }
      
      const socraticQuestion = useSocratic ? this.generateSocraticQuestion() : null
      
      this.addMessage('ai', response, socraticQuestion)
      
      // Update statistics
      this.updateStatistics()
      
      // Save to history
      this.conversationHistory.push({
        role: 'assistant',
        content: response,
        timestamp: new Date().toISOString(),
        socraticLevel: this.socraticLevel,
        course: this.currentCourse
      })
      
      this.saveConversationHistory()
    },
    
    generateSocraticQuestion() {
      const questions = [
        "基于你刚才的回答，你能进一步解释这种现象背后的物理原理吗？",
        "如果我们改变其中一个条件，结果会如何变化？",
        "你认为在实际工程中，这个理论会遇到什么挑战？",
        "能否举一个具体的例子来说明你的观点？",
        "这个概念与我们之前学过的哪些知识有关联？",
        "在中国标准和东盟标准中，这方面有什么不同的要求吗？"
      ]
      
      return questions[Math.floor(Math.random() * questions.length)]
    },
    
    toggleVoiceRecording() {
      if (!this.isRecording) {
        this.startVoiceRecognition()
      } else {
        this.stopVoiceRecognition()
      }
    },
    
    startVoiceRecognition() {
      if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
        this.voiceStatus = '您的浏览器不支持语音识别功能'
        return
      }
      
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition
      this.speechRecognition = new SpeechRecognition()
      
      this.speechRecognition.lang = this.currentLanguage === 'zh' ? 'zh-CN' : 'en-US'
      this.speechRecognition.continuous = false
      this.speechRecognition.interimResults = false
      
      this.speechRecognition.onstart = () => {
        this.isRecording = true
        this.voiceStatus = '正在聆听...请说话'
      }
      
      this.speechRecognition.onresult = (event) => {
        const result = event.results[0][0].transcript
        this.inputMessage = result
        this.voiceStatus = `识别结果: ${result}`
        this.switchInputMode('text')
      }
      
      this.speechRecognition.onerror = (event) => {
        this.voiceStatus = '语音识别出错，请重试'
        this.stopVoiceRecognition()
      }
      
      this.speechRecognition.onend = () => {
        this.stopVoiceRecognition()
      }
      
      this.speechRecognition.start()
    },
    
    stopVoiceRecognition() {
      this.isRecording = false
      this.voiceStatus = '点击按钮开始语音输入'
      if (this.speechRecognition) {
        this.speechRecognition.stop()
      }
    },
    
    selectFile(mode) {
      this.currentFileMode = mode
      this.$refs.fileInput.click()
    },
    
    handleFileSelect(event) {
      const file = event.target.files[0]
      if (file) {
        if (this.currentFileMode === 'handwrite') {
          this.handleHandwriteUpload(file)
        } else if (this.currentFileMode === 'photo') {
          this.handlePhotoUpload(file)
        }
      }
      // Clear the input for next use
      event.target.value = ''
    },
    
    handleDragOver(event) {
      event.currentTarget.classList.add('dragover')
    },
    
    handleDragLeave(event) {
      event.currentTarget.classList.remove('dragover')
    },
    
    handleDrop(event, mode) {
      event.currentTarget.classList.remove('dragover')
      const files = event.dataTransfer.files
      if (files.length > 0) {
        if (mode === 'handwrite') {
          this.handleHandwriteUpload(files[0])
        } else if (mode === 'photo') {
          this.handlePhotoUpload(files[0])
        }
      }
    },
    
    handleHandwriteUpload(file) {
      console.log('处理手写文件:', file.name)
      this.addMessage('user', `[已上传手写图片: ${file.name}]`)
      
      this.isTyping = true
      setTimeout(() => {
        this.isTyping = false
        const response = '我已经识别了你的手写内容。这道关于钢筋混凝土结构的题目很有意思，让我们一步步来分析解决方案。'
        const socraticQuestion = '你认为在这个结构设计中，最关键的考虑因素是什么？'
        this.addMessage('ai', response, socraticQuestion)
      }, 2000)
    },
    
    handlePhotoUpload(file) {
      console.log('处理拍照文件:', file.name)
      this.addMessage('user', `[已上传题目图片: ${file.name}]`)
      
      this.isTyping = true
      setTimeout(() => {
        this.isTyping = false
        const response = '我已经识别了图片中的题目内容。这是一道关于建筑施工技术的选择题，让我为你详细解答每个选项。'
        const socraticQuestion = '在分析选项之前，你能先说说你对这个问题的初步理解吗？'
        this.addMessage('ai', response, socraticQuestion)
      }, 2500)
    },
    
    loadExercise(exerciseId) {
      const exercise = this.exercises.find(e => e.id === exerciseId)
      if (exercise) {
        this.inputMessage = exercise.question
        this.sendMessage()
        
        // Scroll to chat section
        this.$el.querySelector('.chat-section').scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        })
      }
    },
    
    clearChat() {
      if (confirm('确定要清空所有对话记录吗？')) {
        this.messages = []
        this.conversationHistory = []
        this.socraticLevel = 0
        
        const course = this.courses.find(c => c.id === this.currentCourse)
        this.addMessage('ai', `对话已清空。我是你的AI辅导助手，正在为你提供${course.title}课程的专业辅导。有什么问题尽管问我！`)
      }
    },
    
    exportChat() {
      let exportText = '课后AI辅导系统对话记录\n'
      exportText += '='.repeat(50) + '\n'
      
      const course = this.courses.find(c => c.id === this.currentCourse)
      exportText += `课程: ${course.title}\n`
      exportText += `导出时间: ${new Date().toLocaleString()}\n\n`
      
      this.messages.forEach(message => {
        const sender = message.sender === 'user' ? '学生' : 'AI助手'
        exportText += `${sender}: ${message.content}\n`
        if (message.socraticQuestion) {
          exportText += `[引导问题]: ${message.socraticQuestion}\n`
        }
        exportText += '\n'
      })
      
      const blob = new Blob([exportText], { type: 'text/plain;charset=utf-8' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `AI辅导对话记录_${new Date().toISOString().split('T')[0]}.txt`
      a.click()
      URL.revokeObjectURL(url)
    },
    
    openSettings() {
      // In a real application, this would open a settings dialog
      // For now, we'll just show an alert
      alert('设置功能正在开发中...')
    },
    
    updateStatistics() {
      // 使用组件方法更新统计数据
      const questionsIndex = this.statistics.findIndex(s => s.label === '今日问答次数')
      if (questionsIndex !== -1 && this.$refs.statsComponent) {
        const currentCount = parseInt(this.statistics[questionsIndex].value)
        const newValue = String(currentCount + 1)
        this.statistics[questionsIndex].value = newValue
        // 使用组件提供的方法更新
        this.$refs.statsComponent.updateStat(this.statistics[questionsIndex].id, newValue)
      }
    },
    
    handleFeatureClick(feature) {
      console.log('功能特色点击:', feature)
      // 可以添加更多交互逻辑，比如跳转到相关功能页面
      this.$router.push(`/feature/${feature.id}`)
    },
    
    handleStatClick(stat) {
      console.log('统计数据点击:', stat)
      // 可以显示详细的统计图表或历史记录
      alert(`查看 ${stat.label} 的详细信息`)
    },
    
    saveConversationHistory() {
      try {
        const data = {
          history: this.conversationHistory,
          course: this.currentCourse,
          language: this.currentLanguage,
          timestamp: new Date().toISOString()
        }
        localStorage.setItem('ai_tutor_history', JSON.stringify(data))
      } catch (error) {
        console.error('保存对话历史失败:', error)
      }
    },
    
    loadConversationHistory() {
      try {
        const saved = localStorage.getItem('ai_tutor_history')
        if (saved) {
          const data = JSON.parse(saved)
          this.conversationHistory = data.history || []
          
          // Restore last few messages if they exist
          if (this.conversationHistory.length > 0) {
            const lastFew = this.conversationHistory.slice(-3)
            lastFew.forEach(msg => {
              this.messages.push({
                sender: msg.role === 'user' ? 'user' : 'ai',
                content: msg.content
              })
            })
          }
        }
      } catch (error) {
        console.error('加载对话历史失败:', error)
      }
    },
    
    createFloatingElements() {
      // Create initial floating elements
      for (let i = 0; i < 8; i++) {
        setTimeout(() => {
          this.addFloatingElement()
        }, i * 1000)
      }
      
      // Continue creating elements
      setInterval(() => {
        if (this.floatingElements.length < 10) {
          this.addFloatingElement()
        }
      }, 5000)
    },
    
    addFloatingElement() {
      const element = {
        style: {
          left: Math.random() * 100 + '%',
          width: Math.random() * 40 + 20 + 'px',
          height: Math.random() * 40 + 20 + 'px',
          animationDelay: Math.random() * 20 + 's',
          animationDuration: Math.random() * 20 + 20 + 's'
        }
      }
      
      this.floatingElements.push(element)
      
      // Remove element after animation
      setTimeout(() => {
        const index = this.floatingElements.indexOf(element)
        if (index > -1) {
          this.floatingElements.splice(index, 1)
        }
      }, 40000)
    }
  }
}
</script>

<style scoped>
/* Reset for this component */
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

/* CSS Variables */
.ai-tutor-system {
  --primary: #2E5CE6;
  --primary-dark: #1E3A8A;
  --primary-light: #60A5FA;
  --secondary: #10B981;
  --accent: #06B6D4;
  --purple: #8B5CF6;
  --pink: #EC4899;
  --orange: #F59E0B;
  --asean: #D97706;
  --china-red: #DC2626;
  --success: #10B981;
  --warning: #F59E0B;
  --danger: #EF4444;
  --bg-white: #FFFFFF;
  --bg-gray-50: #F9FAFB;
  --bg-gray-100: #F3F4F6;
  --bg-gray-200: #E5E7EB;
  --bg-gradient: linear-gradient(135deg, #EBF5FF 0%, #F0F9FF 50%, #FFF7ED 100%);
  --text-primary: #111827;
  --text-secondary: #6B7280;
  --text-tertiary: #9CA3AF;
  --border-light: #E5E7EB;
  --border-medium: #D1D5DB;
  --shadow-xs: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
  --shadow-sm: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06);
  --shadow-md: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  --shadow-lg: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
  --shadow-xl: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  --shadow-2xl: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  --radius-sm: 8px;
  --radius-md: 12px;
  --radius-lg: 16px;
  --radius-xl: 24px;
  --radius-2xl: 32px;
  --transition-base: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

/* Root Styles */
.ai-tutor-system {
  font-family: -apple-system, BlinkMacSystemFont, 'SF Pro Display', 'Segoe UI', 'PingFang SC', 'Microsoft YaHei', sans-serif;
  background: var(--bg-gradient);
  color: var(--text-primary);
  line-height: 1.6;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  min-height: 100vh;
  position: relative;
  overflow-x: hidden;
}

/* Background decoration */
.bg-decoration {
  position: fixed;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  z-index: -1;
  overflow: hidden;
  pointer-events: none;
}

.bg-circle {
  position: absolute;
  border-radius: 50%;
  opacity: 0.03;
}

.bg-circle-1 {
  width: 600px;
  height: 600px;
  background: var(--primary);
  top: -300px;
  right: -200px;
}

.bg-circle-2 {
  width: 400px;
  height: 400px;
  background: var(--asean);
  bottom: -200px;
  left: -100px;
}

.bg-circle-3 {
  width: 300px;
  height: 300px;
  background: var(--china-red);
  top: 50%;
  left: 10%;
}

/* Hero Section */
.hero-section {
  padding: 40px 0 60px;
  text-align: center;
  position: relative;
}

.hero-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

.hero-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: white;
  padding: 8px 20px;
  border-radius: 100px;
  font-size: 14px;
  font-weight: 600;
  color: var(--primary);
  box-shadow: var(--shadow-md);
  margin-bottom: 24px;
}

.hero-badge-dot {
  width: 8px;
  height: 8px;
  background: var(--success);
  border-radius: 50%;
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.5; transform: scale(0.8); }
}

.hero-title {
  font-size: 56px;
  font-weight: 800;
  line-height: 1.1;
  margin-bottom: 24px;
  background: linear-gradient(135deg, var(--china-red) 0%, var(--primary) 50%, var(--asean) 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  letter-spacing: -0.02em;
}

.hero-subtitle {
  font-size: 20px;
  color: var(--text-secondary);
  max-width: 700px;
  margin: 0 auto 40px;
  line-height: 1.6;
}

.hero-mascot {
  width: 180px;
  height: 180px;
  margin: 0 auto 40px;
  position: relative;
  animation: float 6s ease-in-out infinite;
}

@keyframes float {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-20px); }
}

.mascot-container {
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, #FFE4E1 0%, #FFF7ED 50%, #E6E6FA 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 20px 40px rgba(217, 119, 6, 0.2);
  position: relative;
  overflow: hidden;
}

.mascot-container::before {
  content: '';
  position: absolute;
  width: 100%;
  height: 100%;
  background: radial-gradient(circle at 30% 30%, rgba(255, 255, 255, 0.8) 0%, transparent 60%);
}

.mascot-text {
  font-size: 80px;
  font-weight: 800;
  background: linear-gradient(135deg, var(--china-red) 0%, var(--asean) 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  z-index: 1;
}

.language-controls {
  display: flex;
  justify-content: center;
  gap: 12px;
  margin-bottom: 32px;
}

.language-btn {
  padding: 8px 20px;
  background: white;
  border: 1px solid var(--border-light);
  border-radius: var(--radius-lg);
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: var(--transition-base);
}

.language-btn.active {
  background: linear-gradient(135deg, var(--primary) 0%, var(--asean) 100%);
  color: white;
  border-color: transparent;
}

.language-btn:hover:not(.active) {
  border-color: var(--primary);
}

/* Course Selection Section */
.course-section {
  max-width: 1200px;
  margin: 0 auto 60px;
  padding: 0 20px;
}

.section-header {
  margin-bottom: 48px;
  text-align: center;
}

.section-title {
  font-size: 24px;
  font-weight: 700;
  color: var(--text-primary);
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  letter-spacing: 0.5px;
}

.section-title-icon {
  font-size: 24px;
  line-height: 1;
  display: inline-flex;
  align-items: center;
}

.section-title-text {
  line-height: 1.2;
  display: inline-flex;
  align-items: center;
}

.section-subtitle {
  font-size: 14px;
  color: var(--text-secondary);
  text-align: center;
  max-width: 800px;
  margin: 0 auto;
  line-height: 1.6;
  font-weight: 400;
  opacity: 0.8;
}

.section-subtitle {
  font-size: 18px;
  color: var(--text-secondary);
  text-align: center;
}

.course-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 20px;
}

.course-card {
  background: white;
  border-radius: var(--radius-lg);
  padding: 24px;
  cursor: pointer;
  transition: var(--transition-base);
  border: 1px solid var(--border-light);
  position: relative;
  overflow: hidden;
}

.course-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, var(--primary) 0%, var(--asean) 100%);
  opacity: 0;
  transition: var(--transition-base);
}

.course-card:hover {
  transform: translateY(-4px);
  box-shadow: var(--shadow-lg);
  border-color: var(--primary-light);
}

.course-card:hover::before {
  opacity: 0.05;
}

.course-card.active {
  border: 2px solid var(--primary);
  box-shadow: var(--shadow-lg);
}

.course-card.active::before {
  opacity: 0.1;
}

.course-icon {
  width: 48px;
  height: 48px;
  background: var(--bg-gray-100);
  border-radius: var(--radius-md);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  margin-bottom: 16px;
  position: relative;
  z-index: 1;
}

.course-title {
  font-weight: 600;
  font-size: 16px;
  color: var(--text-primary);
  line-height: 1.4;
  position: relative;
  z-index: 1;
  margin-bottom: 8px;
}

.course-desc {
  font-size: 14px;
  color: var(--text-secondary);
  position: relative;
  z-index: 1;
}

/* Main Chat Section */
.chat-section {
  max-width: 720px;
  margin: 0 auto 80px;
  padding: 0 20px;
}

.chat-container {
  background: white;
  border-radius: var(--radius-2xl);
  box-shadow: var(--shadow-xl);
  overflow: hidden;
  position: relative;
  border: 1px solid var(--border-light);
}

.chat-header {
  padding: 20px 24px;
  border-bottom: 1px solid var(--border-light);
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: var(--bg-gray-50);
}

.chat-title {
  display: flex;
  align-items: center;
  gap: 12px;
}

.chat-title-text {
  font-size: 18px;
  font-weight: 700;
  color: var(--text-primary);
}

.chat-badge {
  background: var(--success);
  color: white;
  padding: 4px 12px;
  border-radius: 100px;
  font-size: 12px;
  font-weight: 600;
}

.chat-controls {
  display: flex;
  align-items: center;
  gap: 12px;
}

.chat-control-btn {
  width: 36px;
  height: 36px;
  background: white;
  border: 1px solid var(--border-light);
  border-radius: var(--radius-md);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: var(--transition-base);
  font-size: 16px;
}

.chat-control-btn:hover {
  background: var(--primary);
  color: white;
  border-color: var(--primary);
}

.chat-messages {
  height: 500px;
  overflow-y: auto;
  padding: 20px 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.message {
  display: flex;
  gap: 12px;
  align-items: flex-start;
}

.message.user {
  flex-direction: row-reverse;
}

.message-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 14px;
  flex-shrink: 0;
}

.message-avatar.ai {
  background: linear-gradient(135deg, var(--primary) 0%, var(--asean) 100%);
  color: white;
}

.message-avatar.user {
  background: var(--bg-gray-200);
  color: var(--text-primary);
}

.message-content {
  max-width: 70%;
  background: white;
  border: 1px solid var(--border-light);
  border-radius: var(--radius-lg);
  padding: 16px 20px;
  font-size: 14px;
  line-height: 1.6;
  box-shadow: var(--shadow-sm);
}

.message.user .message-content {
  background: linear-gradient(135deg, var(--primary) 0%, var(--asean) 100%);
  color: white;
  border: none;
}

.socratic-question {
  background: linear-gradient(135deg, rgba(46, 92, 230, 0.1) 0%, rgba(217, 119, 6, 0.1) 100%);
  border: 2px solid var(--primary-light);
  border-radius: var(--radius-lg);
  padding: 16px 20px;
  margin: 8px 0;
}

.socratic-label {
  font-size: 12px;
  font-weight: 600;
  color: var(--primary);
  margin-bottom: 8px;
}

.typing-indicator {
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--text-secondary);
  font-size: 14px;
}

.typing-dots {
  display: flex;
  gap: 4px;
}

.typing-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--text-secondary);
  animation: typing 1.4s infinite ease-in-out;
}

.typing-dot:nth-child(2) { animation-delay: 0.2s; }
.typing-dot:nth-child(3) { animation-delay: 0.4s; }

@keyframes typing {
  0%, 60%, 100% { transform: translateY(0); }
  30% { transform: translateY(-10px); }
}

/* Input Area */
.chat-input-area {
  padding: 20px 24px;
  border-top: 1px solid var(--border-light);
  background: var(--bg-gray-50);
}

.input-controls {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
  flex-wrap: wrap;
  justify-content: center;
}

.input-mode-btn {
  padding: 8px 16px;
  background: white;
  border: 1px solid var(--border-light);
  border-radius: var(--radius-md);
  font-size: 14px;
  cursor: pointer;
  transition: var(--transition-base);
  display: flex;
  align-items: center;
  gap: 6px;
}

.input-mode-btn.active {
  background: var(--primary);
  color: white;
  border-color: var(--primary);
}

.input-mode-btn:hover:not(.active) {
  border-color: var(--primary);
}

.chat-input-wrapper {
  display: flex;
  align-items: flex-end;
  gap: 12px;
  background: white;
  border-radius: var(--radius-xl);
  padding: 8px;
  box-shadow: var(--shadow-sm);
}

.chat-input {
  flex: 1;
  min-height: 48px;
  max-height: 120px;
  padding: 12px 16px;
  border: none;
  font-size: 16px;
  font-family: inherit;
  resize: none;
  outline: none;
  color: var(--text-primary);
  background: transparent;
  line-height: 1.4;
}

.chat-input::placeholder {
  color: var(--text-tertiary);
}

.send-button {
  width: 48px;
  height: 48px;
  background: linear-gradient(135deg, var(--primary) 0%, var(--primary-dark) 100%);
  border: none;
  border-radius: var(--radius-lg);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: var(--transition-base);
  flex-shrink: 0;
}

.send-button:hover {
  transform: scale(1.05);
  box-shadow: 0 8px 16px rgba(46, 92, 230, 0.3);
}

.send-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
}

.send-button svg {
  width: 20px;
  height: 20px;
  fill: white;
}

/* File Upload */
.file-upload-area {
  border: 2px dashed var(--border-medium);
  border-radius: var(--radius-lg);
  padding: 32px;
  text-align: center;
  cursor: pointer;
  transition: var(--transition-base);
  margin-bottom: 16px;
}

.file-upload-area:hover {
  border-color: var(--primary);
  background: var(--bg-gray-50);
}

.file-upload-area.dragover {
  border-color: var(--primary);
  background: linear-gradient(135deg, rgba(46, 92, 230, 0.1) 0%, rgba(217, 119, 6, 0.1) 100%);
}

.file-upload-icon {
  font-size: 48px;
  color: var(--text-tertiary);
  margin-bottom: 12px;
}

.file-upload-text {
  color: var(--text-secondary);
  font-size: 14px;
}

.file-upload-hint {
  font-size: 12px;
  color: var(--text-tertiary);
  margin-top: 4px;
}

/* Voice Recognition */
.voice-control {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  background: white;
  border-radius: var(--radius-lg);
  margin-bottom: 12px;
  box-shadow: var(--shadow-sm);
}

.voice-button {
  width: 48px;
  height: 48px;
  background: var(--china-red);
  border: none;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: var(--transition-base);
  color: white;
  font-size: 20px;
}

.voice-button.recording {
  animation: pulse-red 1.5s infinite;
}

@keyframes pulse-red {
  0%, 100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(220, 38, 38, 0.7); }
  50% { transform: scale(1.1); box-shadow: 0 0 0 10px rgba(220, 38, 38, 0); }
}

.voice-status {
  flex: 1;
  font-size: 14px;
  color: var(--text-secondary);
}

/* Exercise Recommendations */
.exercise-section {
  padding: 80px 0;
  background: var(--bg-gradient);
}

.exercise-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

.exercise-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 24px;
  margin-top: 48px;
}

.exercise-card {
  background: white;
  border-radius: var(--radius-xl);
  padding: 24px;
  cursor: pointer;
  transition: var(--transition-base);
  border: 1px solid var(--border-light);
  box-shadow: var(--shadow-md);
}

.exercise-card:hover {
  transform: translateY(-4px);
  box-shadow: var(--shadow-xl);
}

.exercise-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 16px;
}

.exercise-title {
  font-size: 18px;
  font-weight: 700;
  color: var(--text-primary);
  line-height: 1.4;
}

.difficulty-badge {
  padding: 4px 12px;
  border-radius: var(--radius-md);
  font-size: 12px;
  font-weight: 600;
  flex-shrink: 0;
}

.difficulty-easy {
  background: var(--success);
  color: white;
}

.difficulty-medium {
  background: var(--warning);
  color: white;
}

.difficulty-hard {
  background: var(--danger);
  color: white;
}

.exercise-meta {
  display: flex;
  align-items: center;
  gap: 16px;
  font-size: 14px;
  color: var(--text-secondary);
  margin-bottom: 12px;
}

.exercise-desc {
  font-size: 14px;
  color: var(--text-secondary);
  line-height: 1.6;
}

/* Floating Elements */
.floating-elements {
  position: fixed;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  pointer-events: none;
  z-index: -1;
  overflow: hidden;
}

.floating-element {
  position: absolute;
  border-radius: 50%;
  background: linear-gradient(135deg, var(--primary) 0%, var(--asean) 100%);
  opacity: 0.03;
  animation: float-element 20s infinite linear;
}

@keyframes float-element {
  0% {
    transform: translateY(100vh) rotate(0deg);
  }
  100% {
    transform: translateY(-100px) rotate(360deg);
  }
}

/* Scrollbar styling */
.chat-messages::-webkit-scrollbar {
  width: 8px;
}

.chat-messages::-webkit-scrollbar-track {
  background: var(--bg-gray-50);
  border-radius: 4px;
}

.chat-messages::-webkit-scrollbar-thumb {
  background: var(--border-medium);
  border-radius: 4px;
}

.chat-messages::-webkit-scrollbar-thumb:hover {
  background: var(--text-tertiary);
}

/* Responsive */
@media (max-width: 768px) {
  .hero-title {
    font-size: 36px;
  }

  .hero-subtitle {
    font-size: 16px;
  }

  .course-grid,
  .exercise-grid {
    grid-template-columns: 1fr;
  }

  .chat-messages {
    height: 400px;
  }

  .input-controls {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px;
  }

  .message-content {
    max-width: 85%;
  }

  .chat-input {
    font-size: 16px;
  }

  .section-title {
    font-size: 28px;
  }

  .exercise-section {
    padding: 60px 0;
  }
}

@media (max-width: 480px) {
  .hero-title {
    font-size: 28px;
  }

  .hero-mascot {
    width: 120px;
    height: 120px;
  }
  
  .mascot-text {
    font-size: 50px;
  }

  .input-controls {
    grid-template-columns: 1fr;
  }

  .chat-container {
    margin: 0 -10px;
    border-radius: 0;
  }

  .floating-elements {
    display: none;
  }

  .chat-header {
    flex-wrap: wrap;
    gap: 12px;
  }

  .chat-controls {
    order: -1;
    width: 100%;
  }

  .section-header {
    padding: 0 20px;
  }
}

/* Focus styles for accessibility */
button:focus-visible,
textarea:focus-visible,
.course-card:focus-visible,
.exercise-card:focus-visible,
.file-upload-area:focus-visible {
  outline: 3px solid var(--primary);
  outline-offset: 2px;
}

/* Animation performance optimization */
@media (prefers-reduced-motion: reduce) {
  * {
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
    scroll-behavior: auto !important;
  }
}
</style>